import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Segmented, riskFromZone, fiscalYearOf } from './primitives';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const METRIC_OPTIONS = [
  { value: 'zScore', label: 'Book-equity Z' },
  { value: 'netMargin', label: 'Net margin' },
  { value: 'currentRatio', label: 'Current ratio' },
  { value: 'debtToEquity', label: 'Debt/Equity' }
];

const METRIC_FORMAT = {
  zScore: (v) => (v == null ? 'Not computable' : Number(v).toFixed(2)),
  netMargin: (v) => (v == null ? 'Not computable' : `${Number(v).toFixed(1)}%`),
  currentRatio: (v) => (v == null ? 'Not computable' : Number(v).toFixed(2)),
  debtToEquity: (v) => (v == null ? 'Not computable' : Number(v).toFixed(2))
};

/**
 * Chart.js inline plugin: shades the safe/grey/distress bands behind the
 * Z-Score series using the thresholds the scoring engine already reports.
 * Purely visual - thresholds come from the payload, nothing is invented.
 */
const bandPlugin = {
  id: 'frRiskBands',
  beforeDraw(chart, args, opts) {
    if (!opts || !opts.thresholds) return;
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.y) return;
    const { safe, distress } = opts.thresholds;
    const yTop = chartArea.top;
    const yBottom = chartArea.bottom;
    const ySafe = Math.max(yTop, Math.min(yBottom, scales.y.getPixelForValue(safe)));
    const yDistress = Math.max(yTop, Math.min(yBottom, scales.y.getPixelForValue(distress)));

    ctx.save();
    ctx.fillStyle = 'rgba(15, 123, 82, 0.06)';
    ctx.fillRect(chartArea.left, yTop, chartArea.right - chartArea.left, ySafe - yTop);
    ctx.fillStyle = 'rgba(154, 103, 0, 0.07)';
    ctx.fillRect(chartArea.left, ySafe, chartArea.right - chartArea.left, yDistress - ySafe);
    ctx.fillStyle = 'rgba(180, 35, 24, 0.06)';
    ctx.fillRect(chartArea.left, yDistress, chartArea.right - chartArea.left, yBottom - yDistress);
    ctx.restore();
  }
};

ChartJS.register(bandPlugin);

const METRIC_NOUN = {
  zScore: 'Book-equity Altman Z',
  netMargin: 'Net margin',
  currentRatio: 'Current ratio',
  debtToEquity: 'Debt-to-equity'
};

/**
 * Conclusion-style chart title, computed from the series.
 * The Z series is book equity so years compare. It is not the Condition score.
 */
const conclusionTitle = (periods, metric) => {
  const withValues = periods.filter((p) => p[metric] != null);
  if (withValues.length < 2) return null;
  const first = withValues[0];
  const last = withValues[withValues.length - 1];
  const a = Number(first[metric]);
  const b = Number(last[metric]);
  const fmt = METRIC_FORMAT[metric];
  const verb = b > a ? 'rose' : b < a ? 'fell' : 'held steady';
  const noun =
    metric === 'zScore' && last.formulaLabel
      ? `${last.formulaLabel} on book equity`
      : METRIC_NOUN[metric];
  let title = `${noun} ${verb}`;
  if (b !== a) title += ` from ${fmt(a)} to ${fmt(b)}`;
  else title += ` at ${fmt(b)}`;
  title += ` between ${fiscalYearOf(first.fiscalDateEnding)} and ${fiscalYearOf(last.fiscalDateEnding)}`;
  if (metric === 'zScore') {
    title += ' — year-to-year series, not the Condition score';
  }
  return title;
};

const TrendPanel = ({ trend, thresholds }) => {
  const [metric, setMetric] = useState('zScore');
  const [view, setView] = useState('chart');

  const periods = useMemo(() => (trend && trend.available ? trend.periods : []), [trend]);
  const bandThresholds = thresholds || trend?.thresholds;

  const chartData = useMemo(() => {
    const labels = periods.map((p) => fiscalYearOf(p.fiscalDateEnding));
    const values = periods.map((p) => (p[metric] == null ? null : Number(p[metric])));
    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: '#1f4e79',
          backgroundColor: '#1f4e79',
          borderWidth: 1.75,
          pointRadius: 3,
          pointHoverRadius: 4,
          tension: 0,
          spanGaps: true
        }
      ]
    };
  }, [periods, metric]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          backgroundColor: '#101623',
          titleFont: { weight: '600' },
          callbacks: {
            label: (ctx) =>
              `${METRIC_OPTIONS.find((m) => m.value === metric)?.label}: ${METRIC_FORMAT[metric](ctx.parsed.y)}`
          }
        },
        frRiskBands: metric === 'zScore' && bandThresholds ? { thresholds: bandThresholds } : false
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#6b7484', font: { size: 11 } }
        },
        y: {
          grid: { color: '#eef0f4' },
          border: { display: false },
          ticks: {
            color: '#6b7484',
            font: { size: 11 },
            callback: (value) =>
              metric === 'netMargin'
                ? `${Number(value).toFixed(1)}%`
                : Number(value).toFixed(2)
          }
        }
      }
    }),
    [metric, bandThresholds]
  );

  if (!trend || !trend.available || periods.length === 0) {
    return (
      <p className="fr-method-note">
        Multi-year trend unavailable - analysis reflects the latest reported period only.
      </p>
    );
  }

  const title = conclusionTitle(periods, metric);

  return (
    <div className="fr-trend-panel">
      {title ? <h5 className="fr-chart-title">{title}</h5> : null}
      <div className="fr-panel-toolbar">
        <Segmented
          ariaLabel="Trend metric"
          options={METRIC_OPTIONS}
          value={metric}
          onChange={setMetric}
          small
        />
        <Segmented
          ariaLabel="Trend view"
          options={[
            { value: 'chart', label: 'Chart' },
            { value: 'table', label: 'Table' }
          ]}
          value={view}
          onChange={setView}
          small
        />
      </div>

      {view === 'chart' ? (
        <div className="fr-chart-wrap">
          <Line data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="fr-table-wrap">
          <table className="fr-table">
            <thead>
              <tr>
                <th>Fiscal year</th>
                <th className="fr-cell-num">Book-equity Z</th>
                <th>Risk</th>
                <th className="fr-cell-num">Net margin</th>
                <th className="fr-cell-num">Current ratio</th>
                <th className="fr-cell-num">Debt/Equity</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period, index) => {
                const risk = riskFromZone(period.riskZone);
                return (
                  <tr key={index}>
                    <td className="fr-cell-strong fr-num">{fiscalYearOf(period.fiscalDateEnding)}</td>
                    <td className="fr-cell-num fr-cell-strong">{METRIC_FORMAT.zScore(period.zScore)}</td>
                    <td className={`fr-text-${risk.tone}`}>{risk.label}</td>
                    <td className="fr-cell-num">{METRIC_FORMAT.netMargin(period.netMargin)}</td>
                    <td className="fr-cell-num">{METRIC_FORMAT.currentRatio(period.currentRatio)}</td>
                    <td className="fr-cell-num">{METRIC_FORMAT.debtToEquity(period.debtToEquity)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {trend.methodNote ? <p className="fr-method-note">{trend.methodNote}</p> : null}
    </div>
  );
};

export default TrendPanel;
